<template>
  <div
    v-if="content.preview && isVideo"
    class="video-container"
  >
    <Feature :featureSlug="featureNames.CAROUSEL_POSTS">
      <div
        class="carousel-selection-container"
        v-if="isCarouselEnabledForKind"
      >
        <v-btn
          v-if="!isMediaSelected"
          icon
          size="small"
          class="selection-button"
          color="primary"
          data-product-fruits="content-creator-video-carousel"
          variant="outlined"
          @click.stop="() => handleCarouselImageToggle(content)"
        >
          <v-icon>mdi-view-carousel-outline</v-icon>
        </v-btn>
        <v-btn
          v-else
          icon
          size="small"
          class="selection-button selected"
          color="primary"
          variant="flat"
          data-product-fruits="content-creator-video-carousel-selected"
          @click.stop="() => handleCarouselImageToggle(content)"
        >
          {{ carouselImagePosition }}
        </v-btn>
      </div>
    </Feature>
    <video
      v-show="!isLoading"
      :src="content.preview"
      class="w-full h-full video-player"
      controls
      @click="handleClick"
    />
  </div>

  <ImageEditWrapper
    v-else-if="content.preview"
    :key="`image-${content.id}-${isImageReverted}-${hasLocalEdits}`"
    :class="{ 'image-edit-wrapper--portrait': isPortraitKind }"
    :image="content.url || content.preview"
    :image-id="content.id"
    :image-type="getImageType(content)"
    :provider="content.provider || selectedImageSourceVariant"
    :external-id="content.externalId || content.id"
    :topic-id="topicId"
    :can-revert="computedCanRevert"
    :is-local-image="computedIsEdited"
    :is-license-required="content.isLicenseRequired"
    :file-id="content.fileId"
    :history="content.history"
    @image-updated="handleImageUpdate"
    @image-reverted="handleImageRevert"
  >
    <canvas
      v-if="isStockImage && !isLoading && !isPortraitKind"
      ref="previewCanvasRef"
      class="w-full h-full img"
      @click="handleClick"
    />
    <Feature :featureSlug="featureNames.CAROUSEL_POSTS">
      <div
        class="carousel-selection-container"
        v-if="isCarouselEnabledForKind"
      >
        <v-tooltip
          :text="
            isImageSelected
              ? $t('content-creator.carousel.click-to-deselect')
              : $t('content-creator.carousel.select-multiple-images')
          "
        >
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              v-if="!isImageSelected"
              icon
              size="small"
              class="selection-button"
              color="primary"
              data-product-fruits="content-creator-image-carousel"
              variant="outlined"
              @click.stop="() => handleCarouselImageToggle(content)"
            >
              <v-icon>mdi-view-carousel-outline</v-icon>
            </v-btn>
            <v-btn
              v-else
              icon
              size="small"
              class="selection-button selected"
              color="primary"
              variant="flat"
              data-product-fruits="content-creator-image-carousel-selected"
              @click.stop="() => handleCarouselImageToggle(content)"
              v-bind="props"
            >
              {{ carouselImagePosition }}
            </v-btn>
          </template>
        </v-tooltip>
      </div>
    </Feature>
    <r-img
      :src="content.preview"
      v-show="!isLoading"
      class="w-full h-full img"
      :class="{ 'img--portrait': isPortraitKind }"
      :aspect-ratio="displayAspectRatio"
      @click="handleClick"
    >
      <template v-slot:error>
        <r-img
          :src="notFound"
          class="w-full h-full"
        />
      </template>
    </r-img>
  </ImageEditWrapper>

  <div
    v-else
    class="justify-center w-full h-full px-6 d-flex flex-column align-center"
  >
    <div
      v-if="isLoading"
      class="justify-center d-flex align-center"
    >
      <ProgressCircularTimed
        :duration="10000"
        :completed="!isLoading"
      />
    </div>

    <div
      v-else-if="content.id === 'uploadImage'"
      class="justify-center d-flex flex-column align-center"
    >
      <!-- 
      <CropImageUpload
        show-drop-zone
        :min-height="1000"
        :min-width="1000"
        accept="image/*"
        :ratio="1"
        :auto-crop-area="1"
        :save="fetch"
        ask-for-image-rights
        :multiple="canCreateCarouselPost"
      />
      -->
      <CropImageUpload
        show-drop-zone
        accept="image/*"
        :auto-crop-area="1"
        :save="fetch"
        :channel-name="uploadChannelName"
        :expected-aspect-ratio="uploadExpectedAspectRatio"
        ask-for-image-rights
        :multiple="canCreateCarouselPost"
      />
    </div>

    <div
      v-else-if="content.id === 'uploadVideo'"
      class="justify-center d-flex flex-column align-center"
    >
      <VideoUpload
        show-drop-zone
        :save="fetch"
        ask-for-video-rights
        :multiple="false"
      />
    </div>

    <div
      v-else-if="content.id === 'noMaterials' || content.id === 'noImage'"
      class="justify-center d-flex flex-column align-center"
    >
      <p>
        {{ $t('content-creator.creator.no-material.0') }}
        <a
          href="#"
          @click.prevent="content.selectStock"
          >{{ $t('content-creator.creator.no-material.1') }}</a
        >
        {{ $t('content-creator.creator.no-material.2') }}
        <a
          href="#"
          @click.prevent="content.selectAi"
          >{{ $t('content-creator.creator.no-material.3') }}</a
        >
        {{ $t('content-creator.creator.no-material.4') }}
      </p>
    </div>

    <div
      v-else
      class="justify-center d-flex flex-column align-center"
    >
      <div class="pa-6">
        {{ content.text || $t('content-creator.creator.no-suitable-image') }}
      </div>
      <UpgradeInfoDialog
        v-if="showUpselling"
        show-info
      >
        <template v-slot="{ props }">
          <v-btn
            class="mb-6"
            v-bind="props"
          >
            {{ $t('content-creator.upselling.generate-personalized-image') }}
          </v-btn>
        </template>
      </UpgradeInfoDialog>
      <v-tooltip
        :disabled="canGenerateImage"
        location="top"
      >
        <template #activator="{ props }">
          <div v-bind="props">
            <v-btn
              color="primary"
              :variant="showUpselling ? 'outlined' : 'elevated'"
              :disabled="!canGenerateImage"
              data-testid="content-creator-generate-image-btn"
              @click="fetch"
            >
              {{ content.button }}
            </v-btn>
          </div>
        </template>
        <span>{{ $t('content-creator.generate-personalized-image.disabled-tooltip') }}</span>
      </v-tooltip>
    </div>
  </div>
</template>
<script>
import notFound from '@/assets/img/image-not-found-icon.png'
import CropImageUpload from '@/components/CropImageUpload.vue'
import { useImageSentry } from '@/components/image-edit/composables/useImageSentry'
import ImageEditWrapper from '@/components/image-edit/ImageEditWrapper.vue'
import ProgressCircularTimed from '@/components/ProgressCircularTimed.vue'
import UpgradeInfoDialog from '@/components/UpgradeInfoDialog.vue'
import VideoUpload from '@/components/VideoUpload.vue'
import { useBranding } from '@/composables/useBranding'
import ImageType from '@/lib/enums/ImageType'
// @ts-ignore
import featureNames from '@/lib/featureNames'
import { drawCroppedPreview } from '@/lib/imageCropping'
import { isExpertUser } from '@/lib/productTypes'
import { showSnackbarMessage } from '@/lib/snackbarMessages'
import { Feature } from '@/plugins/feature-config'

// Import KindType enum (used indirectly through ratioMap)
import _KindType from '../enums/KindType'
// @ts-ignore
import ratioMap from '../lib/ratioMap.js'

export default {
  components: {
    ProgressCircularTimed,
    CropImageUpload,
    VideoUpload,
    ImageEditWrapper,
    Feature,
    UpgradeInfoDialog,
  },
  props: {
    content: {
      type: Object,
      default: () => ({}),
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    selectedImageSourceType: {
      type: String,
      default: '',
    },
    selectedImageSourceVariant: {
      type: String,
      default: '',
    },
    topicId: {
      type: String,
      required: true,
    },
    isStockPhotosReady: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { isPlatformBottimmo } = useBranding()
    return { isPlatformBottimmo }
  },
  inject: {
    textKind: {
      default: null,
    },
    canCreateCarouselPost: { default: () => false },
    isCarouselMode: { default: () => false },
    selectedCarouselImages: { default: () => [] },
    handleCarouselImageToggle: { default: () => () => {} },
    canGeneratePersonalizedImage: { default: () => true },
    auth: {
      default: () => ({
        user: {},
      }),
    },
  },
  data() {
    return {
      uploadedImage: null,
      notFound,
      isImageReverted: false, // Track if the current image was reverted
      hasLocalEdits: false, // Track if the image has local edits
      previewCanvasRef: null,
      sentry: null,
      featureNames,
    }
  },
  created() {
    // Initialize Sentry helper
    this.sentry = useImageSentry({
      imageId: this.content?.id || 'unknown',
      imageType: this.getImageType() || 'unknown',
      topicId: this.topicId,
      provider: this.content?.provider || this.selectedImageSourceVariant,
      externalId: this.content?.externalId || this.content?.id,
    })
  },
  mounted() {
    // Initialize local state based on current content
    this.initializeLocalState()
    // Initialize preview for stock images
    if (this.isStockImage && this.content.preview && !this.isPortraitKind) {
      this.initializeStockPreview()
    }
  },
  watch: {
    // Reset local state when content changes (e.g., switching images)
    'content.id': {
      handler() {
        this.initializeLocalState()
      },
      immediate: false,
    },
    // Watch for changes in the preview URL and stock photos ready state
    isStockPhotosReady: {
      handler(newValue) {
        if (newValue && this.isStockImage && this.content.preview && !this.isPortraitKind) {
          this.scheduleInitialization()
        }
      },
    },
    'content.preview': {
      handler(newPreview) {
        if (this.isStockPhotosReady && this.isStockImage && newPreview && !this.isPortraitKind) {
          this.scheduleInitialization()
        }
      },
    },
    // Watch for aspect ratio changes
    aspectRatio: {
      handler() {
        if (
          this.isStockPhotosReady &&
          this.isStockImage &&
          this.content.preview &&
          !this.isPortraitKind
        ) {
          this.scheduleInitialization()
        }
      },
    },
  },
  computed: {
    isVideo() {
      return (
        this.content?.type === 'video' || this.content?.url?.match(/\.(mp4|mov|avi|wmv|webm)$/i)
      )
    },
    isMediaSelected() {
      return this.isImageSelected
    },
    showUpselling() {
      return !isExpertUser(this.auth.user) && this.isPlatformBottimmo
    },
    canGenerateImage() {
      if (!this.canGeneratePersonalizedImage) return false
      return true
    },
    computedCanRevert() {
      // ABSOLUTE OVERRIDE: If image was reverted, never show revert button
      if (this.isImageReverted === true) {
        return false
      }

      // Check if the image type is supported
      const isSupportedType = ['STOCK', 'AI', 'USER'].includes(this.selectedImageSourceType)
      if (!isSupportedType) {
        return false
      }

      // If we have local edits, it can be reverted
      if (this.hasLocalEdits === true) {
        return true
      }

      // Fallback to checking content properties only if no local state is set
      const hasHistory = this.content.history && this.content.history.length > 0
      const hasEditedVersion = !!this.content.originalImageUrl && !!this.content.fileId

      return hasHistory || hasEditedVersion
    },
    computedIsEdited() {
      // ABSOLUTE OVERRIDE: If image was reverted, never show edited chip
      if (this.isImageReverted === true) {
        return false
      }

      // If we have local edits, show edited chip
      if (this.hasLocalEdits === true) {
        return true
      }

      // Fallback to checking content properties only if no local state is set
      const hasHistory = this.content.history && this.content.history.length > 0
      const hasEditedVersion = !!this.content.originalImageUrl && !!this.content.fileId

      return hasHistory || hasEditedVersion
    },
    aspectRatio() {
      // Default aspect ratio if nothing else matches
      const defaultRatio = 3 / 4

      // Check if textKind exists and we're dealing with a stock image
      if (!this.isStockImage) {
        return defaultRatio
      }

      // Defensive check for textKind
      if (!this.textKind) {
        if (this.sentry) {
          this.sentry.captureImageError(
            new Error('textKind injection is missing'),
            'aspectRatio',
            {},
            'ImageBox'
          )
        }
        return defaultRatio
      }

      // Safely get the kind value
      const kind = this.textKind.value || this.textKind

      // Try to get from ratioMap first (single source of truth)
      if (kind && ratioMap[kind]) {
        return ratioMap[kind]
      }

      // If we couldn't find a match, return default
      return defaultRatio
    },
    displayAspectRatio() {
      return this.isPortraitKind ? 9 / 16 : this.aspectRatio
    },
    isStockImage() {
      return this.selectedImageSourceType === 'STOCK' || this.content.provider === 'STOCK'
    },
    isImageSelected() {
      return this.selectedCarouselImages.some((img) => img.id === this.content.id)
    },
    carouselIconHandler() {
      if (!this.isCarouselMode) return 'mdi-view-carousel'
      if (this.isCarouselMode && !this.isImageSelected) return 'mdi-check-circle'
      return ''
    },
    carouselImagePosition() {
      if (!this.isImageSelected) return 0
      return this.selectedCarouselImages.findIndex((img) => img.id === this.content.id) + 1
    },
    uploadChannelName() {
      const kind = this.textKind?.value || this.textKind
      if (!kind) return ''
      const translationKey = kind.replace(/([A-Z])/g, (m) => `-${m.toLowerCase()}`)
      return this.$t(`content-creator.text-kind-types.${translationKey}`)
    },
    uploadExpectedAspectRatio() {
      const kind = this.textKind?.value || this.textKind
      if (!kind || !ratioMap[kind]) return null
      return ratioMap[kind]
    },
    isPortraitKind() {
      return (this.textKind?.value || this.textKind) === 'whatsappStatus'
    },
    isCarouselEnabledForKind() {
      // Enable carousel only for Facebook and Instagram posts
      return (
        this.textKind &&
        ['facebookPost', 'instagramPost'].includes(this.textKind.value || this.textKind)
      )
    },
  },
  methods: {
    initializeLocalState() {
      // Reset all local state first
      this.isImageReverted = false
      this.hasLocalEdits = false
      this.uploadedImage = null

      // Check if content indicates this image has been edited
      const hasHistory = this.content.history && this.content.history.length > 0
      const hasEditedVersion = !!this.content.originalImageUrl && !!this.content.fileId

      if (hasHistory || hasEditedVersion) {
        this.hasLocalEdits = true
      }
    },
    async initializeStockPreview() {
      // Ensure we have all required properties before proceeding
      if (!this.content?.preview || !this.aspectRatio || !this.isStockPhotosReady) {
        return
      }

      const canvas = this.$refs.previewCanvasRef
      if (!canvas) return

      try {
        const success = await drawCroppedPreview(
          this.content.preview,
          canvas,
          this.displayAspectRatio,
          this.sentry,
          'ImageBox'
        )

        if (!success) {
          this.$emit('image-load-error', {
            contentId: this.content?.id,
            error: 'Failed to draw preview for stock image',
          })
        }
      } catch (error) {
        this.sentry.captureImageError(
          error,
          'initializeStockPreview',
          {
            imageUrl: this.content?.preview || 'unknown',
            aspectRatio: this.displayAspectRatio,
            kindType: this.textKind?.value || this.textKind || 'unknown',
          },
          'ImageBox'
        )

        // Emit error event for parent component to handle
        this.$emit('image-load-error', {
          contentId: this.content?.id,
          error: 'Failed to load stock preview image',
        })
      }
    },
    scheduleInitialization() {
      this.$nextTick(() => {
        this.initializeStockPreview()
      })
    },
    async fetch(...args) {
      try {
        if (!this.content?.fetchMore) {
          if (this.sentry) {
            this.sentry.captureImageError(
              new Error('No fetchMore method available on content object'),
              'fetch',
              { contentId: this.content?.id },
              'ImageBox'
            )
          }
          return
        }
        await this.content.fetchMore(...args)
      } catch (error) {
        if (this.sentry) {
          this.sentry.captureImageError(error, 'fetch', { contentId: this.content?.id }, 'ImageBox')
        }
        showSnackbarMessage('error', this.$t('content-creator.creator.error-fetching-image'))
      }
    },
    handleClick() {
      try {
        this.$emit('handleClick')
      } catch (error) {
        if (this.sentry) {
          this.sentry.captureImageError(error, 'handleClick', {}, 'ImageBox')
        }
      }
    },
    getImageType() {
      if (this.selectedImageSourceType) {
        switch (this.selectedImageSourceType) {
          case 'AI':
            return ImageType.AI
          case 'USER':
            return ImageType.USER
          case 'STOCK':
            return ImageType.STOCK
          case 'MATERIAL':
            return ImageType.MATERIAL
          default:
            break
        }
      }
    },
    handleImageUpdate(updatedImage) {
      if (updatedImage) {
        this.uploadedImage = updatedImage
        this.hasLocalEdits = true // Mark as having local edits
        this.isImageReverted = false // Clear reverted state

        // Create updated content object with fileId to indicate it's been edited locally
        const updatedContent = {
          ...this.content,
          preview: updatedImage.url,
          url: updatedImage.url,
          fileId: updatedImage.id || this.content.fileId, // Ensure fileId is set for edited images
          originalImageUrl: updatedImage.originalImageUrl,
          history: updatedImage.history || this.content.history, // Preserve edit history
        }

        // Emit both the image update and content change events
        this.$emit('image-updated', {
          imageData: updatedImage,
          contentId: this.content?.id,
          newPreviewUrl: updatedImage.url,
          originalImageUrl: updatedImage.originalImageUrl,
        })

        // Emit content change to update the carousel
        this.$emit('change', updatedContent)
      }
    },
    handleImageRevert(revertedImage) {
      if (revertedImage) {
        this.uploadedImage = null // Clear the uploaded image reference
        this.isImageReverted = true // Mark as reverted
        this.hasLocalEdits = false // Clear local edits flag

        // When reverted, url is null, so we use originalImageUrl for display
        const displayUrl = revertedImage.url || revertedImage.originalImageUrl

        // Create updated content object with cleared edit indicators
        const updatedContent = {
          ...this.content,
          preview: displayUrl,
          url: displayUrl,
          fileId: null, // Clear fileId since we're back to original
          originalImageUrl: null, // Clear originalImageUrl since we're back to original state
          history: [], // Clear history since we're back to original state
          isReverted: true, // Flag to indicate this was reverted
        }

        // Emit both the image revert and content change events
        this.$emit('image-reverted', {
          imageData: revertedImage,
          contentId: this.content?.id,
          newPreviewUrl: displayUrl,
          originalImageUrl: revertedImage.originalImageUrl,
        })

        // Emit content change to update the carousel
        this.$emit('change', updatedContent)

        // Force reactivity update immediately after revert
        this.$nextTick(() => {
          this.$forceUpdate()
        })
      }
    },
  },
  emits: ['handleClick', 'image-updated', 'image-reverted', 'change', 'image-load-error'],
}
</script>

<style scoped>
.carousel-information-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  padding: 16px;
}
.carousel-information-toggle {
  position: absolute;
  bottom: 16px;
  right: 16px;
}

.carousel-selected {
  border: 3px solid rgb(var(--v-theme-success)) !important;
  border-radius: 8px;
}

.img {
  object-fit: contain;
  cursor: pointer;
  transition: border 0.2s ease;
}

.img--portrait {
  background: #000;
}

.image-edit-wrapper--portrait {
  background: #000;
}

.img--portrait :deep(img) {
  object-fit: contain !important;
  object-position: center center !important;
}

.video-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.video-player {
  object-fit: contain;
  cursor: pointer;
  transition: border 0.2s ease;
}

.carousel-selection-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  padding: 16px;
}

.selection-button {
  position: absolute;
  top: 8px;
  left: 8px;
  background-color: white;
  border-radius: 50%;
  font-size: 14px;
  font-weight: 600;
}

.selection-button.selected {
  background-color: rgb(var(--v-theme-primary));
  color: white;
}
</style>
